import { useState, useRef } from 'react';
import type { Game } from '../hooks/useGames';
import '../styles/game-badges.css';

interface GameCardProps {
    game: Game;
    size?: 'small' | 'medium' | 'large';
    onPlay?: (game: Game) => void | Promise<void>;
    onInfo?: (game: Game) => void;
    onPromoClick?: (promoURL: string) => void;
    rank?: number; // Per le categorie di tipo "rank"
}

const GameCard = ({ game, size = 'medium', onPlay, onPromoClick, rank }: GameCardProps) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const touchMovedRef = useRef(false);

    const handleImageLoad = () => setImageLoaded(true);
    const handleImageError = () => setImageError(true);

    // Formatta il jackpot da centesimi a euro
    const formatJackpot = (valueInCents: number) => {
        const euroValue = valueInCents / 100;
        return euroValue.toLocaleString('it-IT', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Formatta le funzionalità e volatilità
    const formatGameDetails = () => {
        const details = [];

        if (game.info?.functionality && game.info.functionality.length > 0) {
            details.push(game.info.functionality.join(', '));
        }

        if (game.info?.volatility) {
            details.push(`Vol. ${game.info.volatility}`);
        }

        return details.join(' | ');
    };

    const getSizeClasses = () => {
        switch (size) {
            case 'small':
                return 'w-[120px]';
            case 'large':
                return 'w-[200px]';
            default:
                return 'w-[153px]';
        }
    };

    const handleCardClick = () => {
        onPlay?.(game);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        touchMovedRef.current = false;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!touchStartRef.current) return;
        
        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
        
        // If user moved more than 10 pixels, consider it a scroll
        if (deltaX > 10 || deltaY > 10) {
            touchMovedRef.current = true;
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        // Only trigger click if user didn't scroll
        if (!touchMovedRef.current && touchStartRef.current) {
            e.preventDefault();
            handleCardClick();
        }
        touchStartRef.current = null;
        touchMovedRef.current = false;
    };

    return (
        <button
            type="button"
            className={`group relative ${getSizeClasses()} flex-shrink-0 text-left p-0 bg-transparent border-0 block`}
            onClick={handleCardClick}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ WebkitTapHighlightColor: 'transparent', cursor: 'pointer' }}
        >
            {/* Container immagine con proporzioni fisse */}
            <div className="relative w-full h-[190px] rounded-lg overflow-hidden bg-gray-800 pointer-events-none">
                {!imageError && (game.images?.thumbURL || game.images?.landscapeImgURL) ? (
                    <img
                        src={game.images?.thumbURL || game.images?.landscapeImgURL}
                        alt={game.name}
                        className={`w-full h-full object-cover transition-opacity duration-300 ${
                            imageLoaded ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                        <div className="text-center text-white">
                            <div className="text-sm font-bold mb-1">{game.name}</div>
                        </div>
                    </div>
                )}

                {!imageLoaded && !imageError && (
                    <div className="absolute inset-0 bg-gray-800 animate-pulse" />
                )}

                {/* Badge NEW - in alto a sinistra */}
                {game.isNew && (
                    <div className="absolute top-2 left-2 w-8 h-8">
                        <img
                            src="/new@2x.png"
                            alt="NEW"
                            className="w-full h-full object-contain"
                        />
                    </div>
                )}

                {/* Badge PROMO - in alto a sinistra (spostato se c'è anche NEW) */}
                {game.hasPromo && (
                    <div
                        className={`absolute top-2 w-8 h-8 z-10 cursor-pointer ${
                            game.isNew ? 'left-12' : 'left-2'
                        }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (game.promoURL && onPromoClick) {
                                onPromoClick(game.promoURL);
                            }
                        }}
                        onTouchStart={(e) => {
                            e.stopPropagation();
                        }}
                        onTouchEnd={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (game.promoURL && onPromoClick) {
                                onPromoClick(game.promoURL);
                            }
                        }}
                        style={{ WebkitTapHighlightColor: 'transparent', pointerEvents: 'auto' }}
                    >
                        <img
                            src="/promo-game@2x.png"
                            alt="PROMO"
                            className="w-full h-full object-contain pointer-events-none"
                        />
                    </div>
                )}

                {/* Badge Esclusiva - fascia gialla sopra il jackpot o in basso se non c'è jackpot */}
                {game.info?.exclusive === 1 && (
                    <div className={`absolute left-0 right-0 px-2 py-1 badge-exclusive ${
                        game.info?.jackpot && game.info?.jackpotValue ? 'bottom-6' : 'bottom-0'
                    }`}>
                        <div className="text-xs font-bold text-center">
                            ESCLUSIVA
                        </div>
                    </div>
                )}

                {/* Badge Jackpot - fascia nera in basso */}
                {game.info?.jackpot && game.info?.jackpotValue && (
                    <div className="absolute bottom-0 left-0 right-0 px-2 py-1 badge-jackpot">
                        <div className="text-xs font-bold text-center">
                            JP {formatJackpot(game.info.jackpotValue)} €
                        </div>
                    </div>
                )}

                {/* Numero ranking - sopra l'immagine con 12px dal basso */}
                {rank && (
                    <div className="absolute z-10" style={{ bottom: '12px' }}>
                        <div className="text-white text-5xl font-black"
                             style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                            {rank.toString().padStart(2, '0')}
                        </div>
                    </div>
                )}
            </div>

            {/* Informazioni del gioco - altezza fissa 43px */}
            <div className="game-info-container pointer-events-none">
                <h3 className="game-title" title={game.name}>
                    {game.name}
                </h3>
                <div className="game-subtitle">
                    {formatGameDetails()}
                </div>
            </div>
        </button>
    );
};

export default GameCard;